import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import { TranslateService } from '@ngx-translate/core';
import { transformExtent } from 'ol/proj';
import { Observable, catchError, map, of } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../../../utils/src/lib/services/api-url.service';
import { AutocompleteElement, SearchItem, SearchMode, SolrResponse } from '../../../interfaces/mapSearch.interface';
import { ConfigSidebarService } from '../../../services/config-sidebar.service';
import { FeatureSelectService } from '../../../services/feature-select.service';
import { MapService } from '../../../services/map.service';

@Injectable({ providedIn: 'root' })
export class MapSearchService {
  private MARKER_TITLE: string = 'PLACES_MARKER';

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    private configSidebarService: ConfigSidebarService,
    private ebwModalService: EbwModalService,
    private featureSelectService: FeatureSelectService,
    private httpClient: HttpClient,
    private mapService: MapService,
    private translateService: TranslateService,
    private _apiUrlService: ApiUrlService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  get markerTitle() {
    return this.MARKER_TITLE;
  }

  // --------------------------------------------------------------------------------------------------------------------

  private get defaultSearchMode(): SearchMode {
    const styleConfig = this.configSidebarService.getStyleConfiguration();

    return {
      tooltipLabel: 'Ricerca',
      base64Icon: styleConfig.googleSearch.iconBase64,
      action: 'search'
    };
  }

  // --------------------------------------------------------------------------------------------------------------------

  /**
   * Recupera le varie modalità pr la barra di ricerca
   *
   * default: ricerca google
   * @param additionalNodes (es. ricerca asset)
   */
  getSearchModes(additionalNodes?: SearchMode[], isGoogleKeyEnabled: boolean = false): SearchMode[] {
    const defaultSearchMode = isGoogleKeyEnabled ? [this.defaultSearchMode] : [];

    if (!additionalNodes || !additionalNodes.length) {
      return defaultSearchMode;
    }
    return [...defaultSearchMode, ...additionalNodes];
  }

  // --------------------------------------------------------------------------------------------------------------------

  centerMapOnPoint(geometry: google.maps.places.PlaceGeometry) {
    const { location, viewport } = geometry;
    const positioning = [location.lng(), location.lat()];
    const extendToTransform = [
      viewport.getSouthWest().lng(),
      viewport.getSouthWest().lat(),
      viewport.getNorthEast().lng(),
      viewport.getNorthEast().lat()
    ];
    // Cambio la proiezione della geometria da EPSG:4326 a EPSG:3857
    const extent = transformExtent(extendToTransform, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY);
    // centro la mappa sulla geometria ottenuta
    this.mapService.centerMapOnCoordinatesAndExtent(positioning, extent);
    // rimuovo evenutali precedenti marker
    this.mapService.removeMarker(this.markerTitle);
    // ne aggiungo uno dove centro la mappa
    this.mapService.addMarker(this.markerTitle, positioning);
  }

  // --------------------------------------------------------------------------------------------------------------------

  getAutocompleteOpts(term: string, filterBy: string): Observable<SearchItem[]> {
    const opts = this.featureSelectService.getHttpOptions();
    const url = `${this._apiUrlService.createUrl(API_URLS.MAP.CORE.SEARCH)}?term=${term}&filter=${filterBy}`;
    return this.httpClient.get(url, opts).pipe(
      map((res: SolrResponse) => {
        if (res?.response?.docs) {
          return res.response.docs.map((_: AutocompleteElement) => ({
            desc: _.description,
            type: _.type,
            urns: _.url
          }));
        }
        return [];
      }),
      catchError((error) => {
        const title = this.translateService.instant('common.error');
        const content = this.translateService.instant('common.data_retrieving_error');
        this.ebwModalService.openErrorModal({ title, content });
        console.error('[MapSearchService][getAutocompleteOpts]', error);
        return of([]); // Restituisce un array vuoto per continuare lo stream
      })
    );
  }

  // ----------------------------------------------------------------------------------------------------------------

  async onAutocompleteSelectionChanged(urn: string) {
    try {
      const response = await this.featureSelectService.selectByUrns([urn]);
      if (!response) {
        throw new Error();
      }
      // deep copy di tutta la response
      const currentFeatureResponse = safeJsonClone(response.result);
      // processo vero e proprio
      await this.handleSearchItemSelection(currentFeatureResponse);
      // ritorno oggetto al componente perchè si aggiorni internamente
      return currentFeatureResponse;
    } catch (error) {
      const title = this.translateService.instant('common.error');
      const content = this.translateService.instant('common.data_retrieving_error');
      this.ebwModalService.openErrorModal({ title, content });
      return null;
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  async handleSearchItemSelection(currentFeatureResponse: any) {
    let feature = currentFeatureResponse.features[0];
    // clear the current feature from being displayed
    this.featureSelectService.clearCurrentFeature(true);
    // se l'oggetto non ha la geometria
    if (!feature.geometry) {
      // cerco di ereditare le geometrie da asset contenitori
      const inheritedGeometry = await this.featureSelectService.getPlacedAssetGeometry(feature);
      // se riesco ad ottenerla la assegno
      if (inheritedGeometry) {
        feature.geometry = inheritedGeometry;
      }
    }
    // seleziona e posiziona oggetto su mappa
    this.featureSelectService.selectFeatureFromSearch(currentFeatureResponse);
  }
}
